/**
 * ForgotPassword.tsx
 * Password reset request page - sends reset email via Supabase
 */

import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../contexts/AuthContext';

const ForgotPassword: React.FC = () => {
  const { resetPassword } = useAuth();
  
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await resetPassword(email);
      setIsSubmitted(true);
    } catch (err: any) {
      // Don't reveal if email exists or not for security
      // Still show success to prevent email enumeration
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Success state - email sent
  if (isSubmitted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F0F0 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: 3, sm: 6 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              backgroundColor: 'white',
              borderRadius: 3,
              border: '1px solid #F0F0F0',
              textAlign: 'center',
            }}
          >
            <CheckCircleOutlineIcon 
              sx={{ 
                fontSize: 80, 
                color: 'success.main', 
                mb: 3 
              }} 
            />
            
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Check Your Email
            </Typography>
            
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              If an account exists for <strong>{email}</strong>, you'll receive a password reset link shortly.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              The link will expire in 24 hours. If you don't see the email, check your spam folder.
            </Typography>
            
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              startIcon={<ArrowBackIcon />}
              sx={{ mb: 2 }}
            >
              Back to Sign In
            </Button>
            
            <Typography variant="body2" color="text.secondary">
              Didn't receive the email?{' '}
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  setIsSubmitted(false);
                  setEmail('');
                }}
                sx={{ cursor: 'pointer' }}
              >
                Try again
              </Link>
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Request form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F0F0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 3, sm: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: 3,
            border: '1px solid #F0F0F0',
          }}
        >
          {/* CHECK SET GO Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              marginBottom: 4,
            }}
          >
            <Box
              component="img"
              src="/logo.svg"
              alt="CHECK SET GO Logo"
              sx={{
                width: 60,
                height: 72,
                objectFit: 'contain',
              }}
            />
            <Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 800, 
                  lineHeight: 0.9,
                  color: 'text.primary',
                }}
              >
                CHECK SET GO
              </Typography>
            </Box>
          </Box>

          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
            Forgot Password?
          </Typography>
          
          <Typography color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            No worries! Enter your email address and we'll send you a link to reset your password.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading || !email}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Send Reset Link'}
            </Button>
            
            <Box sx={{ textAlign: 'center' }}>
              <Link 
                component={RouterLink} 
                to="/login" 
                variant="body2"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                <ArrowBackIcon fontSize="small" />
                Back to Sign In
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;

